import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { extractErr } from '../../api/error-message';
import { Card } from '../../components/card';
import { PageHeader } from '../../components/page-header';
import { SkeletonRows } from '../../components/skeleton';
import { Symbol } from '../../components/symbol';
import { Toggle } from '../../components/toggle';
import { useConfirm } from '../../components/confirm-dialog';
import { TextField } from '../../components/crud-drawer';

// App content. Wired to /admin/app-content.
//
// What each app shows on its Help and support screen, and the privacy policy and
// terms a seller or delivery partner agrees to when registering. One document
// per (type, app). The apps read only published documents; a legal document's
// version goes up every time its title or wording changes, and registration
// records the version each person agreed to.

type ContentType = 'HELP' | 'PRIVACY_POLICY' | 'TERMS';
type Audience = 'CUSTOMER' | 'SELLER' | 'DELIVERY';

interface Faq {
  q: string;
  a: string;
}

interface HelpData {
  email: string;
  phone: string;
  whatsapp: string;
  hours: string;
  faqs: Faq[];
}

interface ContentDoc {
  contentType: ContentType;
  audience: Audience;
  title: string;
  body: string;
  data: HelpData | null;
  version: number;
  isPublished: boolean;
  publishedAt: string | null;
  updatedAt: string;
}

const AUDIENCES: { key: Audience; label: string; app: string }[] = [
  { key: 'CUSTOMER', label: 'Customers', app: 'customer app' },
  { key: 'SELLER', label: 'Sellers', app: 'seller app' },
  { key: 'DELIVERY', label: 'Delivery partners', app: 'partner app' },
];

const TYPES: { key: ContentType; label: string; icon: string }[] = [
  { key: 'HELP', label: 'Help and support', icon: 'support_agent' },
  { key: 'PRIVACY_POLICY', label: 'Privacy policy', icon: 'privacy_tip' },
  { key: 'TERMS', label: 'Terms and conditions', icon: 'gavel' },
];

const EMPTY_HELP: HelpData = { email: '', phone: '', whatsapp: '', hours: '', faqs: [] };

interface Form {
  title: string;
  body: string;
  isPublished: boolean;
  help: HelpData;
}

function toForm(d: ContentDoc): Form {
  return {
    title: d.title,
    body: d.body,
    isPublished: d.isPublished,
    help: { ...EMPTY_HELP, ...(d.data ?? {}), faqs: (d.data?.faqs ?? []).map((f) => ({ ...f })) },
  };
}

const sameForm = (a: Form, b: Form) => JSON.stringify(a) === JSON.stringify(b);

export function AppContentPage() {
  const [rows, setRows] = useState<ContentDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [audience, setAudience] = useState<Audience>('SELLER');
  const [type, setType] = useState<ContentType>('PRIVACY_POLICY');
  const [form, setForm] = useState<Form | null>(null);
  const [busy, setBusy] = useState(false);
  const { confirm } = useConfirm();

  const load = async () => {
    try {
      const { data } = await api.get<ContentDoc[]>('/admin/app-content');
      setRows(data);
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const doc = useMemo(
    () => rows.find((r) => r.audience === audience && r.contentType === type) ?? null,
    [rows, audience, type],
  );
  const saved = useMemo(() => (doc ? toForm(doc) : null), [doc]);
  const dirty = !!form && !!saved && !sameForm(form, saved);

  // Reset the editor whenever a different document is selected or reloaded.
  useEffect(() => {
    setForm(saved);
  }, [saved]);

  const select = async (nextAudience: Audience, nextType: ContentType) => {
    if (dirty) {
      const ok = await confirm({
        title: 'Discard your changes?',
        description: 'You have unsaved edits to this document. Switching now throws them away.',
        confirmLabel: 'Discard',
        tone: 'danger',
      });
      if (!ok) return;
    }
    setAudience(nextAudience);
    setType(nextType);
  };

  const isHelp = type === 'HELP';
  const reworded = !!form && !!saved && (form.title !== saved.title || form.body !== saved.body);

  const save = async () => {
    if (!doc || !form) return;
    if (!form.title.trim()) {
      toast.error('Enter a title');
      return;
    }
    if (!isHelp && form.isPublished && !form.body.trim()) {
      toast.error('Write the document before publishing it');
      return;
    }
    const faqs = form.help.faqs.map((f) => ({ q: f.q.trim(), a: f.a.trim() })).filter((f) => f.q || f.a);
    if (isHelp && faqs.some((f) => !f.q || !f.a)) {
      toast.error('Every FAQ needs both a question and an answer');
      return;
    }
    if (!isHelp && doc.isPublished && reworded) {
      const ok = await confirm({
        title: `Publish version ${doc.version + 1}?`,
        description:
          'The wording changed, so this becomes a new version. Everyone registering from now on agrees to the new text; people who already registered keep their record against the version they accepted.',
        confirmLabel: 'Save new version',
        tone: 'primary',
      });
      if (!ok) return;
    } else if (doc.isPublished && !form.isPublished) {
      const ok = await confirm({
        title: 'Unpublish this document?',
        description: isHelp
          ? 'The app falls back to showing no contact details or FAQs.'
          : 'The app stops showing it, and registration no longer asks people to accept it.',
        confirmLabel: 'Unpublish',
        tone: 'danger',
      });
      if (!ok) return;
    }

    setBusy(true);
    try {
      const { data } = await api.put<ContentDoc>(`/admin/app-content/${type}/${audience}`, {
        title: form.title.trim(),
        body: form.body,
        isPublished: form.isPublished,
        ...(isHelp
          ? {
              data: {
                email: form.help.email.trim(),
                phone: form.help.phone.trim(),
                whatsapp: form.help.whatsapp.trim(),
                hours: form.help.hours.trim(),
                faqs,
              },
            }
          : {}),
      });
      setRows((cur) => cur.map((r) => (r.audience === audience && r.contentType === type ? data : r)));
      toast.success(data.isPublished ? 'Saved — the app shows it within a minute' : 'Saved as unpublished');
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setBusy(false);
    }
  };

  const setHelp = (patch: Partial<HelpData>) =>
    setForm((f) => (f ? { ...f, help: { ...f.help, ...patch } } : f));
  const setFaq = (i: number, patch: Partial<Faq>) =>
    setHelp({ faqs: form!.help.faqs.map((f, j) => (j === i ? { ...f, ...patch } : f)) });
  const moveFaq = (i: number, by: -1 | 1) => {
    const faqs = [...form!.help.faqs];
    const [item] = faqs.splice(i, 1);
    faqs.splice(i + by, 0, item);
    setHelp({ faqs });
  };

  const appName = AUDIENCES.find((a) => a.key === audience)!.app;

  return (
    <div className="space-y-6">
      <PageHeader
        title="App content"
        description="Help and support details, privacy policy and terms shown in each app. Sellers and delivery partners accept the published policy and terms when they register."
      />

      <div role="tablist" aria-label="App" className="tabs">
        {AUDIENCES.map((a) => (
          <button
            key={a.key}
            type="button"
            role="tab"
            aria-selected={audience === a.key}
            onClick={() => void select(a.key, type)}
            className="tab"
          >
            {a.label}
          </button>
        ))}
      </div>

      {loading ? (
        <Card>
          <SkeletonRows rows={6} />
        </Card>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
          <nav aria-label="Documents" className="space-y-2">
            {TYPES.map((t) => {
              const d = rows.find((r) => r.audience === audience && r.contentType === t.key);
              const active = type === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => void select(audience, t.key)}
                  aria-current={active ? 'true' : undefined}
                  className={`panel flex w-full items-center gap-3 p-3.5 text-left transition-colors ${
                    active ? '!border-admin bg-admin/5' : 'hover:bg-ink-50'
                  }`}
                >
                  <Symbol name={t.icon} className="text-ink-700" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-[14px] font-semibold text-ink-900">{t.label}</span>
                    <span className="block text-[12.5px] text-ink-500">
                      {d?.isPublished ? 'Published' : 'Not published'}
                      {d && t.key !== 'HELP' ? ` · v${d.version}` : ''}
                    </span>
                  </span>
                </button>
              );
            })}
          </nav>

          {doc && form ? (
            <Card padding="lg" className="space-y-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <h2 className="text-[16px] font-semibold text-ink-900">
                    {TYPES.find((t) => t.key === type)!.label} — {appName}
                  </h2>
                  <p className="mt-0.5 text-[13px] text-ink-500">
                    {!isHelp && `Version ${doc.version}${reworded ? ` → ${doc.version + 1} on save` : ''} · `}
                    Last updated {new Date(doc.updatedAt).toLocaleString()}
                  </p>
                </div>
                <label className="flex items-center gap-2.5 text-[14px] text-ink-900">
                  Published
                  <Toggle
                    checked={form.isPublished}
                    onChange={(v) => setForm({ ...form, isPublished: v })}
                    aria-label="Published"
                  />
                </label>
              </div>

              <TextField
                label="Title"
                value={form.title}
                onChange={(v) => setForm({ ...form, title: v })}
                maxLength={200}
                required
              />

              {isHelp ? (
                <>
                  <label className="block">
                    <span className="field-label mb-1.5">Introduction</span>
                    <textarea
                      className="input"
                      rows={2}
                      value={form.body}
                      onChange={(e) => setForm({ ...form, body: e.target.value })}
                      placeholder="One or two lines shown at the top of the screen"
                    />
                  </label>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <TextField label="Support email" value={form.help.email} onChange={(v) => setHelp({ email: v })} placeholder="support@utsaaha.in" />
                    <TextField label="Support phone" value={form.help.phone} onChange={(v) => setHelp({ phone: v })} placeholder="+91 98765 43210" maxLength={20} />
                    <TextField label="WhatsApp number" value={form.help.whatsapp} onChange={(v) => setHelp({ whatsapp: v })} placeholder="+91 98765 43210" maxLength={20} />
                    <TextField label="Support hours" value={form.help.hours} onChange={(v) => setHelp({ hours: v })} placeholder="Mon–Sat, 10 AM – 7 PM" maxLength={120} />
                  </div>
                  <p className="field-help">Leave a contact blank to hide it in the app.</p>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="field-label">Frequently asked questions</span>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => setHelp({ faqs: [...form.help.faqs, { q: '', a: '' }] })}
                        disabled={form.help.faqs.length >= 30}
                      >
                        <Symbol name="add" /> Add question
                      </button>
                    </div>
                    {form.help.faqs.length === 0 && (
                      <p className="text-[13.5px] text-ink-500">No FAQs — the app shows only the contact details.</p>
                    )}
                    {form.help.faqs.map((f, i) => (
                      <div key={i} className="rounded-xl border border-ink-200 p-3.5">
                        <div className="flex gap-2">
                          <input
                            className="input flex-1"
                            value={f.q}
                            maxLength={200}
                            onChange={(e) => setFaq(i, { q: e.target.value })}
                            placeholder="Question"
                            aria-label={`Question ${i + 1}`}
                          />
                          <button type="button" className="btn btn-ghost btn-icon" aria-label="Move up" disabled={i === 0} onClick={() => moveFaq(i, -1)}>
                            <Symbol name="arrow_upward" />
                          </button>
                          <button type="button" className="btn btn-ghost btn-icon" aria-label="Move down" disabled={i === form.help.faqs.length - 1} onClick={() => moveFaq(i, 1)}>
                            <Symbol name="arrow_downward" />
                          </button>
                          <button
                            type="button"
                            className="btn btn-ghost btn-icon"
                            aria-label="Remove question"
                            onClick={() => setHelp({ faqs: form.help.faqs.filter((_, j) => j !== i) })}
                          >
                            <Symbol name="delete" />
                          </button>
                        </div>
                        <textarea
                          className="input mt-2"
                          rows={2}
                          maxLength={2000}
                          value={f.a}
                          onChange={(e) => setFaq(i, { a: e.target.value })}
                          placeholder="Answer"
                          aria-label={`Answer ${i + 1}`}
                        />
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <label className="block">
                  <span className="field-label mb-1.5">Document</span>
                  <textarea
                    className="input font-mono text-[13.5px]"
                    rows={22}
                    value={form.body}
                    onChange={(e) => setForm({ ...form, body: e.target.value })}
                    placeholder={'# 1. Information we collect\nWe collect…\n\n# 2. How we use it\n…'}
                  />
                  <span className="field-help mt-1.5">
                    Plain text. Start a line with “# ” to make it a heading; leave a blank line between paragraphs.
                  </span>
                </label>
              )}

              <div className="flex flex-wrap items-center justify-end gap-2.5 border-t border-ink-200 pt-4">
                {dirty && <span className="mr-auto text-[13px] text-ink-500">Unsaved changes</span>}
                <button type="button" className="btn btn-secondary" disabled={!dirty || busy} onClick={() => setForm(saved)}>
                  Discard
                </button>
                <button type="button" className="btn btn-primary" disabled={!dirty || busy} onClick={() => void save()}>
                  {busy ? 'Saving…' : 'Save'}
                </button>
              </div>
            </Card>
          ) : (
            <Card>
              <p className="text-[14px] text-ink-500">This document is missing — run the latest database migration.</p>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
